import { AfterViewInit, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import * as $ from 'jquery';

import {
    CalculationCompound,
    CaseWorker, HighestRiskCalculationType, LocationItem, LocationSearchCacheKey,
    LocationSearchOrder, LocationSearchType, RiskAssessmentStatus
} from 'src/app/contracts';
import { CompoundService, LocationService } from 'src/app/services';
import { AppConfig } from 'src/app/app.config';
import { EMPTY, of, Subject, timer } from 'rxjs';
import { debounce, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

class StatusFilter {
    public status: RiskAssessmentStatus;
    public text: string;
}

@Component({
    selector: 'app-search-location',
    templateUrl: './search-location.component.html',
    styleUrls: ['./search-location.component.css']
})
export class SearchLocationComponent implements OnInit, AfterViewInit, OnDestroy {
    public displaySearchResults = false;
    public isSearching = false;
    public initialSearch = true;
    public isContinuing = false;
    public hasMoreSearchResults = false;
    public loading = true;
    public searchOffset = 0;
    public minFactor = 0;
    public maxFactor = null;
    public searchText = '';

    public caseWorkers: Array<CaseWorker>;
    public caseWorkerFilter: CaseWorker;
    public userFilterScreening = false;
    public userFilterText: string;

    public orderBy = LocationSearchOrder.RiskassessmentFactor;
    public orderDescending = true;

    public riskAssessmentStatus = RiskAssessmentStatus; // Make enum accessible from the html-template
    public highestRiskCalculationType = HighestRiskCalculationType;
    public locationSearchOrder = LocationSearchOrder;

    public statusFilters: StatusFilter[] = [
        {
            status: RiskAssessmentStatus.NoRisk,
            text: 'Ingen risiko'
        },
        {
            status: RiskAssessmentStatus.Risk,
            text: 'Risiko'
        }
    ];
    public statusFilter: StatusFilter;
    public compoundList: CalculationCompound[];
    public filteredCompoundList: CalculationCompound[];
    public contaminantName: string;

    public allSearchResults: Array<LocationItem>;
    public filteredSearchResults: Array<LocationItem>;

    protected destroyed$ = new Subject<void>();

    private searchSubject = new Subject<string>();
    private destroySearchDebounce$ = new Subject<void>();

    constructor(
        @Inject(LOCALE_ID) private readonly locale,
        private readonly locationService: LocationService,
        private readonly compoundService: CompoundService,
        private readonly cdref: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        this.userFilterText = 'Vælg sagsbehandler';

        this.searchSubject.pipe(
            debounce(() =>
                timer(2000).pipe(
                    takeUntil(this.destroySearchDebounce$)
                )
            ),
            distinctUntilChanged(),
            filter(() => !this.isSearching),
            takeUntil(this.destroyed$))
            .subscribe(() => {
                this.onSearch();
            });

        const searchView = this.locationService.getLocationsFromCache(LocationSearchCacheKey.Screening);
        if (searchView.total === 0) {
            return;
        }
        this.searchOffset = searchView.items.length;
        this.allSearchResults = searchView.items;
        this.filteredSearchResults = searchView.items;

        this.isSearching = false;
        this.initialSearch = false;
        this.isContinuing = false;
        this.displaySearchResults = searchView.items.length > 0;
        this.hasMoreSearchResults = this.searchOffset + AppConfig.settings.maxItems < searchView.total;
    }

    public ngAfterViewInit(): void {
        this.getCompounds();
        this.onSearch();
        this.cdref.detectChanges();
    }

    public getCompounds(): void {
        const compounds = this.compoundService.getCompoundsFromCache(LocationSearchCacheKey.Compound);
        if (!compounds || compounds.length === 0) {
            this.compoundService.getCompounds()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((res: any) => {
                this.compoundList = res;
                this.compoundService.setCompoundsFromCache(LocationSearchCacheKey.Compound, res);
                this.filteredCompoundList = [...res];
                this.loading = false;
            });
        } else {
            this.compoundList = compounds;
            this.filteredCompoundList = compounds;
            this.loading = false;
        }
    }

    public setOrderBy(newOrderBy: LocationSearchOrder): void {
        if (this.orderBy === newOrderBy) {
            this.orderDescending = !this.orderDescending;
        }
        else {
            this.orderDescending = false;
        }

        this.orderBy = newOrderBy;
        this.searchOffset = 0;

        this.refreshSearch();
    }

    public dkJordLocationReference: string | null | undefined = undefined;

    private refreshSearch(continueSearch: boolean = false): void {
        if (this.isSearching) {
            return;
        }
        this.isSearching = true;
        this.isContinuing = continueSearch;
        this.dkJordLocationReference = null;
        // this.displaySearchResults = continueSearch;
        this.hasMoreSearchResults = false;

        if (continueSearch) {
            this.searchOffset += AppConfig.settings.maxItems;
        }

        const criteria = {
            searchText: this.searchText,
            caseWorker: this.caseWorkerFilter,
            riskAssessmentStatus: this.statusFilter,
            searchType: LocationSearchType.RiskAssessment,
            minFactor: this.minFactor,
            maxFactor: this.maxFactor,
            contaminant: this.contaminantName,
            orderBy: this.orderBy,
            orderDescending: this.orderDescending,
            skip: this.searchOffset,
            take: AppConfig.settings.maxItems
        };
        // requsest search locations
        this.locationService.search(criteria)
            .pipe(
                switchMap((res: any) => {
                    // if no results, continue to search in DK Jord
                    const isEmpty = (!res.items || res.items.length === 0) && res.total === 0;
                    
                    if (!isEmpty) {
                        // have results, return these locations
                        return of(res);
                    }

                    const pattern = /^\d{3}-\d{5}$/;
                    const isValidSearchText = pattern.test(this.searchText);
                    if (!isValidSearchText) {
                        // no need to attemp to search in DK Jord, return empty results
                        // res is empty results here anyway
                        return of(res);
                    }

                    // search location in DKJord
                    return this.locationService.searchDkJordLocations(this.searchText)
                    .pipe(
                        switchMap((dkJordRes: any) => {
                            const fallbackEmpty = (!dkJordRes.items || dkJordRes.items.length === 0) && dkJordRes.total === 0;
                            if (fallbackEmpty) {
                                return of(dkJordRes);
                            }
                            // show force location dialog
                            if (dkJordRes.items.length === 1 && dkJordRes.total === 1) {
                                this.showDKJordLocation(dkJordRes.items[0]);
                            }
                            // clear the result to avoid showing location in the list
                            return of({ items: [], total: 0, continuationToken: null});
                        })
                    );
                })
            )
            .subscribe((res: any) => {
                this.processSearchResults(res, continueSearch);
            });
    }

    private processSearchResults(res: any, continueSearch: boolean): void {
         if (continueSearch) {
            this.allSearchResults = this.allSearchResults.concat(res.items);
        }
        else {
            this.allSearchResults = res.items;
        }

        this.caseWorkers = [];
        this.allSearchResults.forEach(p => {
            if (!p.caseWorker || !p.caseWorker.name) {
                return;
            }
            const existingOnes = this.caseWorkers.filter(c => c.name === p.caseWorker.name);
            if (existingOnes.length > 0) {
                return;
            }
            this.caseWorkers.push(p.caseWorker);
        });

        this.isSearching = false;
        this.isContinuing = false;
        this.initialSearch = false;
        this.displaySearchResults = this.allSearchResults.length > 0;
        this.hasMoreSearchResults = this.searchOffset + AppConfig.settings.maxItems < res.total;

        res.items = this.allSearchResults;
        this.locationService.setLocationsToCache(LocationSearchCacheKey.Screening, res);
    }


    private showDKJordLocation(dkJordLocation: any): void {
        // show dialog to create location from DK Jord
        if (dkJordLocation) {
            $('#toggleForceCreateLocationModalButton').click();
            this.dkJordLocationReference = dkJordLocation.locationReference;
        }
    }

    public forceCreateLocation(locationReference: string): void {
        if (this.dkJordLocationReference && locationReference && this.dkJordLocationReference === locationReference) {
            this.locationService.forceCreateLocation(this.dkJordLocationReference)
            .pipe(
                takeUntil(this.destroyed$),
                finalize(() => {
                    this.refreshSearch();
                })
            )
            .subscribe({
                next: (res: any) => {
                    this.searchText = locationReference;
                },
                error: (err: any) => {
                    this.searchText = '';
                }
            })
        }
    }

    public onKeydown(): void {
        this.onSearch();
    }

    public onInputChanged(): void {
        this.searchSubject.next(this.searchText);
    }

    public onSearch(): void {
        this.searchOffset = 0;
        this.orderBy = LocationSearchOrder.RiskassessmentFactor;
        this.orderDescending = true;
        this.refreshSearch();
    }

    public onClickSearchButton(): void {
        this.destroySearchDebounce$.next();
        this.onSearch();
    }

    public getMoreResults(): void {
        this.refreshSearch(true);
    }

    public filterUser(caseWorker: CaseWorker): void {
        this.caseWorkerFilter = caseWorker;
        this.userFilterText = caseWorker.name;
        this.refreshSearch();
    }

    public filterCaseWorkerClear(): void {
        this.caseWorkerFilter = null;
        this.userFilterScreening = false;
        this.userFilterText = 'Vælg sagsbehandler';
        this.refreshSearch();
    }

    public isScreening(location: any): boolean {
        return location.calculationType === HighestRiskCalculationType.Screening;
    }

    public getValidNumber(val: number): number {
        const result = (!val || isNaN(val) || !Number.isFinite(val)) ? 0 : val;
        return result;
    }

    public getNumberWithSignificantDigits(val: number): string {
        const n = this.getValidNumber(val);
        let digitsInfo = '';
        if (n >= 10.0) { // xx.x
            digitsInfo = '1.1-1';
        }
        else if (n >= 1.0) { // x.xx
            digitsInfo = '1.2-2';
        }
        else if (n >= 0.1) { // 0.xxx
            digitsInfo = '1.3-3';
        }
        else if (n >= 0.01) { // 0.0xxx
            digitsInfo = '1.4-4';
        }
        else { // 0.00xxx
            digitsInfo = '1.5-5';
        }

        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public filterContaminants(contaminantName: string): void {
        if (!contaminantName) {
            this.filteredCompoundList = this.compoundList;
        } else {
            this.filteredCompoundList = this.compoundList.filter(c => c.name.toLowerCase().indexOf(contaminantName.toLowerCase()) >= 0);
        }
        if (!this.contaminantName || this.contaminantName.trim().length === 0){
            this.refreshSearch();
        }
    }

    public ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
